import { type JSX, lazy, type ReactNode, Suspense, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { init as initFederationRuntime } from '@module-federation/runtime';
import { Box } from '@mui/material';
// Import the bare i18next singleton (not the '../../i18n' wrapper module) so
// this file never touches 'react-i18next'. Several unrelated feature tests
// globally mock 'react-i18next' with a partial stub (only `useTranslation`),
// and importing the wrapper here would pull in `initReactI18next` and break
// those tests. `i18next` is shared as a singleton, so this is the same,
// already-initialized instance main.jsx sets up at startup.
import i18n from 'i18next';

import FloatingHeader from '../../components/Header/FloatingHeader';
import { isSafari } from '../../utils/auth/dxp-sso';

const REMOTE_RETRY_DELAY_MS = 150;
const REMOTE_RETRY_COUNT = 3;
const MYCARGILL_REMOTE_ENTRY = '/food/targetPoint.js';
const SHOULD_REGISTER_RUNTIME_REMOTE = !import.meta.env.DEV;

let federationInitPromise: Promise<unknown> | null = null;

function ensureMyCargillRemoteRegistered(): Promise<unknown> {
  if (!SHOULD_REGISTER_RUNTIME_REMOTE) {
    return Promise.resolve();
  }

  if (!federationInitPromise) {
    federationInitPromise = Promise.resolve(
      initFederationRuntime({
        name: 'redcarpet',
        remotes: [
          {
            name: 'mycargill',
            entry: MYCARGILL_REMOTE_ENTRY,
            entryGlobalName: 'mycargill',
            shareScope: 'default',
            type: 'module'
          }
        ]
      })
    );
  }

  return federationInitPromise;
}

function isRemoteRegistrationError(error: unknown): boolean {
  return error instanceof Error && /RUNTIME-004|Failed to locate remote/i.test(error.message);
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => {
    window.setTimeout(resolve, ms);
  });
}

async function loadRemoteWithRetry<T>(loader: () => Promise<T>, retryCount = REMOTE_RETRY_COUNT): Promise<T> {
  let lastError: unknown;

  await ensureMyCargillRemoteRegistered();

  for (let attempt = 0; attempt < retryCount; attempt += 1) {
    try {
      return await loader();
    } catch (error) {
      lastError = error;

      if (!isRemoteRegistrationError(error) || attempt === retryCount - 1) {
        throw error;
      }

      await delay(REMOTE_RETRY_DELAY_MS * (attempt + 1));
    }
  }

  throw lastError;
}

const GlobalNavMyCargill = lazy(() =>
  // @ts-ignore ts-ignore-next-line
  loadRemoteWithRetry(() => import('mycargill/GlobalNavMyCargill')).catch(error => {
    console.error('[MF] Failed to load GlobalNavMyCargill:', error);

    return {
      default: ({ children }: { children?: ReactNode }) => <>{children}</>
    };
  })
);

const FooterMyCargill = lazy(() =>
  // @ts-ignore ts-ignore-next-line
  loadRemoteWithRetry(() => import('mycargill/FooterComponent')).catch(() => ({
    default: () => null
  }))
);

interface TemplateProps {
  children: ReactNode;
}

const Template = ({ children }: TemplateProps): JSX.Element => {
  const { pathname } = useLocation();

  // Safari-only: the shared i18next singleton is sometimes not yet initialized
  // (host's i18n.init() side-effect hasn't run) by the time Safari mounts the
  // MyCargill remote — a JSC timing quirk that doesn't happen in Chrome/Firefox
  const [remoteI18nReady, setRemoteI18nReady] = useState<boolean>(!isSafari() || i18n.isInitialized);

  useEffect(() => {
    if (remoteI18nReady) {
      return undefined;
    }

    const handleInitialized = (): void => setRemoteI18nReady(true);

    i18n.on('initialized', handleInitialized);

    // Guard against a race between the initial state check above and this
    // effect's subscription (init could complete in between).
    if (i18n.isInitialized) {
      setRemoteI18nReady(true);
    }

    return () => {
      i18n.off('initialized', handleInitialized);
    };
  }, [remoteI18nReady]);

  useEffect(() => {
    // @ts-ignore ts-ignore-next-line
    void loadRemoteWithRetry(() => import('mycargill/FibiStyles')).catch(() => undefined);
  }, []);

  return (
    <Box className="app-content">
      <Box className="sprout-template">
        <Suspense fallback={<Box sx={{ height: '64px' }} />}>
          {remoteI18nReady ? (
            <GlobalNavMyCargill>
              <FloatingHeader />
              <Box
                sx={{
                  padding: '0 !important',
                  width: '100%',
                  marginTop: pathname.includes('/form') ? '16px' : '24px !important'
                }}
              >
                {children}
              </Box>
            </GlobalNavMyCargill>
          ) : (
            <>
              <FloatingHeader />
              <Box
                sx={{
                  padding: '0 !important',
                  width: '100%',
                  marginTop: pathname.includes('/form') ? '16px' : '24px !important'
                }}
              >
                {children}
              </Box>
            </>
          )}
        </Suspense>
      </Box>
      <Suspense fallback={<Box sx={{ height: '64px' }} />}>{remoteI18nReady && <FooterMyCargill />}</Suspense>
    </Box>
  );
};

export default Template;
